<template>
  <div class="student-details">
    <v-row>
      <!-- Clasroom Details Card -->
      <v-col cols="12" sm="12" md="12" lg="12" class="py-0">
        <v-card class="mx-4 mt-5 px-4" flat tile color="transparent">
          <v-row>
            <v-col cols="12" class="text-left pb-0">
              <v-row>
                <v-col cols="10">
                  <h1>
                    Classroom : {{ classroom.sections }}
                  </h1>
									<h3>{{ classroom.session ? classroom.session.title : '' }}</h3>
                </v-col>
                <v-col
                  v-if="currentUser.privileges.canEditClassroomDetails"
                  cols="2"
                >
                  <v-btn
                    color="primary"
                    outlined
                    rounded
                    small
                    :to="'/classrooms/edit/' + classroom.id"
                  >
                    Edit
                    <span class="hidden-sm-and-down ml-1">Classroom</span>
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12" md="9">
              <v-row>
                <v-col cols="4" md="3">
                  <h1 class="text-left">{{ classroom.room_no || "-" }}</h1>
                  <p class="mb-0 text-left">Room number</p>
                </v-col>
                <v-col cols="4" md="3">
                  <h1 class="text-left">{{ classroom.room_subjects }}</h1>
                  <p class="mb-0 text-left">Subjects</p>
                </v-col>
                <v-col cols="4" md="3">
                  <h1 class="text-left">{{ classroom.room_users }}</h1>
                  <p class="mb-0 text-left">Students</p>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <!-- Classroom Details Tabs -->
      <v-col cols="12" sm="12" md="12" lg="12">
        <v-container fluid class="ma-5">
          <v-tabs>
            <v-tab class="ml-4">Subjects</v-tab>
            <v-tab>Students</v-tab>
            <!-- Subjects tab item -->
            <v-tab-item class="mx-4 my-4">
              <v-data-table
                :headers="subjectHeaders"
                :items="subjects"
                :items-per-page="-1"
                hide-default-footer
              >
                <template v-slot:item.code="{ item }">
                  <h4>{{ item.code || "-" }}</h4>
                </template>
                <template v-slot:item.name="{ item }">
                  <h4>{{ item.name }}</h4>
                </template>
                <template
                  v-slot:item.faculty="{ item }"
                  v-if="$vuetify.breakpoint.xs"
                >
                  <div v-for="faculty in item.faculty" :key="faculty.id">
                    <p class="mb-0">{{ faculty.full_name }}</p>
                  </div>
                </template>
                <template v-slot:item.faculty="{ item }" v-else>{{
                  `${item.faculty.map((faculty) => " " + faculty.full_name)}`
                }}</template>
              </v-data-table>
            </v-tab-item>

            <!-- Student tab item -->
            <v-tab-item>
              <v-card class="mx-4 my-4" elevation="0">
                <v-card-title>
                  Classroom Students
                  <v-spacer></v-spacer>
                  <v-text-field
                    v-model="search"
                    append-icon="mdi-magnify"
                    label="Search"
                    single-line
                    hide-details
                  ></v-text-field>
                </v-card-title>
                <v-data-table
                  :headers="studentHeaders"
                  :items="students"
                  :search="search"
                  :options.sync="options"
                  :items-per-page="15"
                  :multi-sort="true"
                  :server-items-length="pagination.totalCount"
                  @click:row="clickStudentRow"
                  class="row-pointer"
                >
                  <template v-slot:item.roll_number="{ item }">
                    <h4>{{ item.roll_number || "-" }}</h4>
                  </template>
                  <template v-slot:item.image="{ item }">
                    <v-avatar width="30" height="30" class="mb-2">
                      <img :src="item.image" />
                    </v-avatar>
                  </template>
                  <template v-slot:item.full_name="{ item }">
                    <h4>{{ item.full_name }}</h4>
                  </template>
                  <template v-slot:item.phone="{ item }">
                    <h4>{{ item.phone }}</h4>
                  </template>
                </v-data-table>
              </v-card>
            </v-tab-item>
          </v-tabs>
        </v-container>
      </v-col>
    </v-row>
    <v-snackbar v-model="snackbar.visible">
      <span>{{ snackbar.text }}</span>
      <template>
        <v-btn color="pink" text @click="snackbar.visible = false">Close</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import api from "@api/index";
import endpoints from "@api/repository";

export default {
  name: "ClassroomDetails",
  data() {
    return {
      search: "",
      snackbar: {
        visible: false,
        text: "",
      },
      options: {},
      pagination: {
        totalCount: 0,
        url: endpoints.getClassRoomStudents + `${this.$route.params.id}/`,
      },
      subjectHeaders: [
        { text: "Code", value: "code" },
        { text: "Subject Name", value: "name" },
        { text: "Assigned Teachers", value: "faculty", sortable: false },
      ],
      studentHeaders: [
        { text: "Roll no.", value: "roll_number" },
        { text: "Photo", value: "image", sortable: false },
        { text: "Name", value: "full_name" },
        { text: "Phone Number", value: "phone" },
      ],
      students: [],
      subjects: [],
      classroom: {},
    };
  },
  computed: {
    ...mapGetters(["accessToken", "currentUser"]),
    apiEssentials() {
      return {
        accessToken: this.accessToken,
        handleErrorFunction: this.handleApiErrors,
        setLoading: this.setLoading,
      };
    },
  },
  methods: {
    ...mapActions(["setLoading"]),
    // get staff details from server
    async getClassRoomDetails() {
      this.setLoading(true);
      var response;
      var url = endpoints.getClassRoomDetails + `${this.$route.params.id}/`;

      response = await api.handleApi({
        apiEssentials: this.apiEssentials,
        url: url,
        responseVar: response,
      });

      if (response instanceof Error) {
        this.setLoading(false);
        this.snackbar.text = "Error retrieving details";
        this.snackbar.visible = true;
        return;
      } else {
        this.classroom = response;
      }
      this.setLoading(false);
    },

    async getClassRoomSubjects() {
      this.setLoading(true);
      var response;
      var url = endpoints.getClassRoomSubjects + `${this.$route.params.id}/`;

      response = await api.handleApi({
        apiEssentials: this.apiEssentials,
        url: url,
        responseVar: response,
      });

      if (response instanceof Error) {
        this.setLoading(false);
        this.snackbar.text = "Error retrieving subjects";
        this.snackbar.visible = true;
        return;
      } else {
        this.subjects = response;
      }
      this.setLoading(false);
    },

    async getClassRoomStudents() {
      this.setLoading(true);
      var response;
      var url = this.pagination.url;
      // if search query exists, add to the url as a GET param w/o disturbing other params
      if (this.search) {
        url += (url.split("?")[1] ? "&" : "?") + "query=" + this.search;
      }

      response = await api.handleApi({
        apiEssentials: this.apiEssentials,
        url: url,
        responseVar: response,
      });

      if (response) {
        // console.log(response);
        this.students = response.results;
        this.pagination.totalCount = response.count;
        this.pagination.nextUrl = response.next;
      }
      this.setLoading(false);
    },

    async handleApiErrors(err) {
      this.setLoading(false);
      if (err.response) {
        console.log(err.response);
      } else {
        this.snackbar.text = "Unknown error occurred";
        this.snackbar.visible = true;
      }
    },

    clickStudentRow(value) {
      this.$router.push({ name: "StudentDetails", params: { id: value.id } });
    },
  },
  watch: {
    options: {
      async handler() {
        // console.log("something changed");
        // console.log(this.options);s
        this.pagination.url =
          endpoints.getClassRoomStudents +
          `${this.$route.params.id}/` +
          "?page=" +
          this.options.page;
        if (this.options.sortBy.length > 0) {
          // get url var
          var params = "&o=";
          // iterate over sortBy options and add sortby and sortdesc seperated with commas
          for (var sortOption in this.options.sortBy) {
            params +=
              (this.options.sortDesc[sortOption] ? "" : "-") +
              this.options.sortBy[sortOption];
            if (sortOption != this.options.sortBy.length - 1) params += ",";
          }
          this.pagination.url += params;
        }
        this.getClassRoomStudents();
      },
    },
    search: {
      async handler() {
        var searched = this.search;
        var self = this;
        setTimeout(function () {
          if (searched == self.search) {
            self.getClassRoomStudents();
          }
        }, 500);
      },
    },
  },
  created() {
    this.getClassRoomDetails();
    this.getClassRoomSubjects();
    this.getClassRoomStudents();
  },
};
</script>

<style scoped>
.row-pointer >>> tbody tr :hover {
  cursor: pointer;
}
</style>